import React, { useState, useEffect } from 'react';
import { login } from './api';
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [location, setLocation] = useState(null);
  const [geoError, setGeoError] = useState(false);
  const [geoPermission, setGeoPermission] = useState('prompt');
  const [geolocationConsent, setGeolocationConsent] = useState('prompt');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    checkGeolocationPermission();
  }, []);

  const checkGeolocationPermission = async () => {
    if (!navigator.permissions || !navigator.geolocation) {
      getGeolocation();
      return;
    }

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      setGeoPermission(status.state);
      setGeolocationConsent(status.state);

      if (status.state === 'granted') {
        getGeolocation();
      } else if (status.state === 'prompt') {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
            setGeoError(false);
            setGeoPermission('granted');
            setGeolocationConsent('granted');
          },
          (err) => {
            setGeoError(true);
            setGeoPermission('denied');
            setGeolocationConsent('denied');
            console.warn('User denied geolocation or an error occurred:', err);
          }
        );
      }

      status.onchange = () => {
        setGeoPermission(status.state);
        setGeolocationConsent(status.state);
        if (status.state === 'granted') getGeolocation();
      };
    } catch (err) {
      console.warn('Fallback due to permission check failure:', err);
      getGeolocation();
    }
  };

  const getGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
          setGeoError(false);
          setGeolocationConsent('granted');
        },
        () => {
          setGeoError(true);
          setLocation(null);
          setGeolocationConsent('denied');
        }
      );
    } else {
      setGeoError(true);
    }
  };

  const toggleGeolocation = () => {
    getGeolocation(); // korisnik uvek može pokušati da uključi geolokaciju
  };

  const handleLogin = async () => {
    if (!location) {
      setError('Geolocation is not enabled. Please enable it to proceed.');
      return;
    }

    try {
      const response = await login(email, password, location.lat, location.lon);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('location', JSON.stringify(location));

      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'teacher') {
        navigate('/teacher');
      } else {
        setError('Unknown user role.');
      }
    } catch (err) {
	  console.error('Login error:', err.response?.data || err.message || err);
      setError(err.response?.data?.message || 'Login failed.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-md bg-white p-6 rounded-xl shadow-lg">
  <div className="flex items-center justify-center mb-8 gap-3">
    <img
      src="/logo192.png"
      alt="Logo"
      className="w-12 h-12 shadow"
      width={48}
      height={48}
      style={{ objectFit: "cover" }}
    />
    <h2 className="text-2xl font-bold text-gray-800">TIP Infant - Login</h2>
  </div>

        <input
          type="email"
          placeholder="Email"
          className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          type="password"
          placeholder="Password"
          className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {/* Geolokacija switch */}
        <div className="flex items-center mb-4">
          <label className="mr-4 text-gray-800">
            {geolocationConsent === 'granted'
              ? 'Geolocation is enabled'
              : 'Enable geolocation'}
          </label>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={geolocationConsent === 'granted'}
              onChange={toggleGeolocation}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-blue-600 transition-colors"></div>
            <span className="absolute left-1 top-1 w-4 h-4 bg-white border border-gray-300 rounded-full transition-transform peer-checked:translate-x-full peer-checked:border-white"></span>
          </label>
        </div>

        {geoError && (
          <div className="mb-4 text-red-600 text-sm">
            Geolokacija nije omogućena. <br />
            <button
              onClick={getGeolocation}
              className="mt-2 text-blue-600 underline hover:text-blue-800"
            >
              Try enabling geolocation again
            </button>
          </div>
        )}

        {geoPermission === 'denied' && (
          <div className="mb-4 text-orange-600 text-sm">
            Geolokacija je onemogućena u podešavanjima pregledača. <br />
            <span className="text-gray-600 text-xs">
              (Please enable it manually in your browser settings.)
            </span>
          </div>
        )}

        {error && <p className="mb-4 text-red-600 text-sm">{error}</p>}

	  <button
	    onClick={handleLogin}
	    disabled={geolocationConsent !== 'granted' || !location || !email || !password}
	    className={`w-full py-3 rounded-lg font-semibold transition ${
	      geolocationConsent === 'granted' && location && email && password
	        ? 'bg-blue-600 hover:bg-blue-700 text-white'
	        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
	    }`}
	  >
	    Sign In
	  </button>
      </div>
    </div>
  );
};

export default Login;




